import { useState, useRef, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretRight, faCaretDown } from '@fortawesome/free-solid-svg-icons'
import classnames from 'classnames'
import { useOutsideClick } from '../utils/hooks/useOutsideClick'
import styles from './_dropdown.module.sass'

export interface DropdownOption {
  label: string
  value: string
}

export enum DropdownTheme {
  dark,
  light
}

export interface DropdownProps {
  selected?: DropdownOption
  placeholder?: string
  options: DropdownOption[]
  onChange(DropdownOption): void
  theme?: DropdownTheme
  className?: string
  currentSelected?: string
}

const Dropdown: React.FC<DropdownProps> = (props) => {
  const theme = props.theme || DropdownTheme.dark
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [selected, setSelected] = useState<DropdownOption>(
    props.selected || null
  )

  const ref = useRef()

  useOutsideClick(ref, isDropdownOpen, () => {
    setIsDropdownOpen(false)
  })

  const classes = classnames(styles.dropdown, {
    [styles.isActive]: isDropdownOpen,
    [styles.light]: theme === DropdownTheme.light,
    [props.className]: props.className
  })

  const clickHandler = (option) => {
    setSelected(option)
    setIsDropdownOpen(false)
    props.onChange(option)
  }

  useEffect(() => {
    if (props.currentSelected && props.currentSelected !== props.placeholder) {
      setSelected(null)
    }
  }, [props.currentSelected])

  return (
    <div className={classes} ref={ref}>
      <div className={styles.dropdownTrigger}>
        <button
          className="button"
          aria-haspopup="true"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <span>{selected ? selected.label : props.placeholder}</span>
        </button>
        <FontAwesomeIcon
          className={styles.dropdownIcon}
          icon={isDropdownOpen ? faCaretDown : faCaretRight}
        ></FontAwesomeIcon>
      </div>
      <div className={styles.dropdownMenu} role="menu">
        <div className={styles.dropdownContent}>
          {props.options.map((option, index) => (
            <button
              key={index}
              className={`${styles.dropdownItem} ${
                selected === option ? styles.isActive : ''
              }`}
              onClick={() => clickHandler(option)}
              type="button"
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export default Dropdown
